import { defineBlock, cssText, resolveValue, toNumber, toDisplayString, evaluate, Scope, type Issue } from "@broadpaper/core";
import { renderChart, type ChartData, type ChartHit, type ChartType } from "../charts/chart-svg.js";

export interface ChartSeries {
  id: string;
  label: string;
  /** Expression evaluated per item. */
  value: string;
  color?: string;
}

export interface ChartProps extends Record<string, unknown> {
  chartType: ChartType;
  source: string;
  alias: string;
  label: string;
  series: ChartSeries[];
  height: number;
  title: string;
  showLegend: boolean;
  showValues: boolean;
  showGrid: boolean;
  stacked: boolean;
  smooth: boolean;
  valueFormat: string;
  percentDecimals: number;
  innerLabel: string;
  limit: number | null;
}

export const chartBlock = defineBlock<ChartProps>({
  type: "chart",
  name: "Chart",
  description: "Bar, line, area, pie or donut chart bound to a list",
  category: "visualisation",
  icon: "bar-chart-3",
  keywords: ["graph", "bar", "line", "pie", "donut", "area", "visualisation"],
  defaultProps: () => ({
    chartType: "bar",
    source: "",
    alias: "item",
    label: "item.label",
    series: [{ id: "s1", label: "Value", value: "item.value" }],
    height: 220,
    title: "",
    showLegend: true,
    showValues: false,
    showGrid: true,
    stacked: false,
    smooth: true,
    valueFormat: "",
    percentDecimals: 0,
    innerLabel: "",
    limit: null
  }),
  defaultTextStyle: "body",
  defaultFlow: { keepTogether: true },
  split: "none",
  styleGroups: ["spacing", "background", "border", "flow"],
  bindings: [
    { prop: "source", kind: "collection", alias: "alias" },
    { prop: "label", kind: "value", perItem: true },
    { prop: "series", kind: "list", items: [{ prop: "value", kind: "value", perItem: true }, { prop: "label", kind: "template" }] },
    { prop: "title", kind: "template" },
    { prop: "innerLabel", kind: "template" }
  ],
  inspector: [
    {
      title: "Chart",
      fields: [
        {
          kind: "segmented",
          prop: "chartType",
          label: "Type",
          options: [
            { value: "bar", label: "Bar", icon: "bar-chart-3" },
            { value: "horizontalBar", label: "Horizontal", icon: "bar-chart-horizontal" },
            { value: "line", label: "Line", icon: "line-chart" },
            { value: "area", label: "Area", icon: "area-chart" },
            { value: "pie", label: "Pie", icon: "pie-chart" },
            { value: "donut", label: "Donut", icon: "circle-dot" }
          ]
        },
        { kind: "text", prop: "title", label: "Title" },
        { kind: "number", prop: "height", label: "Height", min: 80, max: 800, unit: "px" }
      ]
    },
    {
      title: "Data",
      fields: [
        { kind: "collection", prop: "source", label: "Data from", placeholder: "Pick a list" },
        { kind: "text", prop: "alias", label: "Item name", description: (p) => `The name each data point goes by, e.g. ${typeof p.alias === "string" && p.alias.trim() ? p.alias.trim() : "item"}.value`, allowTokens: false },
        { kind: "expression", prop: "label", label: "Category label", placeholder: "item.name" },
        { kind: "series", prop: "series", label: "Series", fullWidth: true },
        { kind: "number", prop: "limit", label: "Maximum items", min: 1, max: 1000 },
        { kind: "text", prop: "valueFormat", label: "Value format", placeholder: 'e.g. currency:"GBP":0 or percent', allowTokens: false }
      ]
    },
    {
      title: "Options",
      fields: [
        { kind: "toggle", prop: "showLegend", label: "Legend" },
        { kind: "toggle", prop: "showValues", label: "Value labels" },
        { kind: "toggle", prop: "showGrid", label: "Grid lines", when: (p) => p.chartType !== "pie" && p.chartType !== "donut" },
        { kind: "toggle", prop: "stacked", label: "Stacked", when: (p) => ["bar", "horizontalBar", "line", "area"].includes(String(p.chartType)) },
        { kind: "toggle", prop: "smooth", label: "Smooth lines", when: (p) => p.chartType === "line" || p.chartType === "area" },
        { kind: "number", prop: "percentDecimals", label: "Percent decimals", min: 0, max: 3, when: (p) => p.chartType === "pie" || p.chartType === "donut" },
        { kind: "text", prop: "innerLabel", label: "Centre label", when: (p) => p.chartType === "donut" }
      ]
    }
  ],
  validate: ({ props }) => {
    const issues: Issue[] = [];
    if (!Array.isArray(props.series) || props.series.length === 0) issues.push({ severity: "warning", code: "empty-content", message: "Chart has no series", prop: "series" });
    return issues;
  },
  canvas: { label: (p) => (p.title ? `Chart · ${p.title}` : "Chart") },
  resolve: ({ props, evaluateIn, theme, warn }) => {
    const alias = props.alias || "item";
    let items = Array.isArray(props.source) ? (props.source as unknown[]) : [];
    if (props.limit) items = items.slice(0, props.limit);
    const palette = theme.colors.chart ?? [theme.colors.primary, theme.colors.secondary, theme.colors.accent];
    const categories = items.map((it, index) => {
      try {
        return toDisplayString(evaluateIn(props.label || `${alias}.label`, { [alias]: it, item: it, index: index + 1 }));
      } catch {
        return String(index + 1);
      }
    });
    const series = (Array.isArray(props.series) ? props.series : []).map((s, si) => ({
      label: s.label || `Series ${si + 1}`,
      color: s.color ? resolveValue(s.color, theme, palette[si % palette.length]!) : palette[si % palette.length]!,
      values: items.map((it, index) => {
        try {
          return toNumber(evaluateIn(s.value, { [alias]: it, item: it, index: index + 1 }));
        } catch (e) {
          warn(`Series "${s.label}" failed: ${(e as Error).message}`);
          return null;
        }
      })
    }));
    if (props.chartType === "pie" || props.chartType === "donut") {
      // Pie: one colour per category.
      const s0 = series[0];
      if (s0) {
        const colored = items.map((_, i) => ({ ...s0, color: palette[i % palette.length]! }));
        return { __data: { categories, series: colored.length ? [{ ...s0 }, ...colored.slice(1)] : [] }, __palette: palette } as unknown as ChartProps;
      }
    }
    return { __data: { categories, series } as ChartData, __palette: palette } as unknown as ChartProps;
  },
  render: ({ h, css, props, theme, width, mode, node, format, text, style }) => {
    const p = props as ChartProps & { __data?: ChartData; __palette?: string[] };
    const data: ChartData = p.__data ?? { categories: [], series: [] };
    const isPie = p.chartType === "pie" || p.chartType === "donut";
    if (isPie && data.series.length) {
      const palette = p.__palette ?? theme.colors.chart ?? [];
      data.series = data.categories.map((_, i) => ({ ...data.series[0]!, color: palette[i % Math.max(1, palette.length)] ?? data.series[0]!.color }));
    }
    const formatValue = (n: number) => {
      const f = (p.valueFormat || "").trim();
      if (!f) return new Intl.NumberFormat(format.locale, { notation: Math.abs(n) >= 10000 ? "compact" : "standard", maximumFractionDigits: 2 }).format(n);
      try {
        return toDisplayString(evaluate(`(${n}) | ${f}`, { scope: new Scope({}), format }));
      } catch {
        return String(n);
      }
    };
    const formatPercent = (fr: number) => new Intl.NumberFormat(format.locale, { style: "percent", maximumFractionDigits: p.percentDecimals ?? 0, minimumFractionDigits: p.percentDecimals ?? 0 }).format(fr);
    const pad = style.padding;
    const innerWidth = Math.max(40, width - pad.left - pad.right - (style.border ? style.border.width * 2 : 0));
    const b = node.bindings.source;
    // What each mark stands for, in draw order. A screen renderer turns this
    // into hover, focus and a tooltip (see `enableChartInteractions`); a PDF
    // renderer drops it and draws precisely what it drew before. Recording it
    // is the block's job because the block is the only thing that knows which
    // mark is which datum.
    const hits: ChartHit[] = [];
    const svg = data.categories.length
      ? renderChart(data, { type: p.chartType, width: innerWidth, height: p.height, theme, fontFamily: style.fontFamily, showLegend: p.showLegend, showValues: p.showValues, showGrid: p.showGrid, stacked: p.stacked, smooth: p.smooth, formatValue, formatPercent, innerLabel: typeof p.innerLabel === "string" && p.innerLabel ? p.innerLabel : undefined, hits })
      : h("div", { class: "bp-chart__empty", style: { height: `${p.height}px`, display: "flex", "align-items": "center", "justify-content": "center", color: theme.colors.mutedText, border: `1px dashed ${theme.colors.border}`, "border-radius": `${theme.radius ?? 6}px` } }, mode === "design" ? (b ? "No data in the list" : "Choose a list to chart") : "");
    return h(
      "div",
      {
        class: ["bp-chart", `bp-chart--${p.chartType}`],
        style: cssText(css),
        ...(hits.length ? { "data-bp-chart": JSON.stringify({ type: p.chartType, title: typeof p.title === "string" ? p.title : "", hits }) } : {})
      },
      p.title ? h("div", { class: "bp-chart__title", style: { "font-family": theme.typography.headingFont, "font-weight": "600", "margin-bottom": "6px", color: theme.colors.text } }, text("title")) : null,
      mode === "design" && b ? h("div", { class: "bp-chart__badge", contenteditable: "false" }, b.label) : null,
      svg
    );
  }
});

export const progressBlock = defineBlock<{ value: string; max: number; label: string; caption: string; variant: "bar" | "gauge"; color: string; trackColor: string; showValue: boolean; format: "percent" | "value" | "fraction"; thresholds: Array<{ id: string; upTo: number; color: string }>; height: number }>({
  type: "progress",
  name: "Progress / gauge",
  description: "A value against a maximum, as a bar or gauge",
  category: "visualisation",
  icon: "gauge",
  keywords: ["score", "meter", "gauge", "progress", "percentage"],
  defaultProps: () => ({ value: "", max: 100, label: "Score", caption: "", variant: "bar", color: "$colors.primary", trackColor: "$colors.border", showValue: true, format: "percent", thresholds: [], height: 10 }),
  defaultTextStyle: "body",
  defaultFlow: { keepTogether: true },
  split: "none",
  styleGroups: ["typography", "spacing", "background", "border", "flow"],
  inspector: [
    {
      title: "Value",
      fields: [
        { kind: "binding", prop: "value", label: "Value", valueType: ["number", "percent", "currency"] },
        { kind: "number", prop: "max", label: "Maximum", min: 0.0001, max: 1e12 },
        { kind: "text", prop: "label", label: "Label" },
        { kind: "text", prop: "caption", label: "Caption" }
      ]
    },
    {
      title: "Appearance",
      fields: [
        {
          kind: "segmented",
          prop: "variant",
          label: "Style",
          options: [
            { value: "bar", label: "Bar" },
            { value: "gauge", label: "Gauge" }
          ]
        },
        {
          kind: "segmented",
          prop: "format",
          label: "Show as",
          options: [
            { value: "percent", label: "%" },
            { value: "value", label: "Value" },
            { value: "fraction", label: "x / max" }
          ]
        },
        { kind: "toggle", prop: "showValue", label: "Show value" },
        { kind: "color", prop: "color", label: "Colour" },
        { kind: "color", prop: "trackColor", label: "Track" },
        { kind: "number", prop: "height", label: "Bar height", min: 2, max: 40, unit: "px", when: (p) => p.variant === "bar" },
        { kind: "list", prop: "thresholds", label: "Colour by range", itemLabel: "Range", fullWidth: true, fields: [
          { kind: "number", prop: "upTo", label: "Up to" },
          { kind: "color", prop: "color", label: "Colour" }
        ] }
      ]
    }
  ],
  render: ({ h, css, props, theme, text, mode, node, format }) => {
    const raw = toNumber(props.value);
    const max = Number(props.max) || 100;
    const value = raw ?? 0;
    const frac = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
    let color = resolveValue(props.color, theme, theme.colors.primary);
    const sorted = [...(props.thresholds ?? [])].sort((a, b) => a.upTo - b.upTo);
    for (const t of sorted) {
      if (value <= t.upTo) {
        color = resolveValue(t.color, theme, color);
        break;
      }
    }
    const track = resolveValue(props.trackColor, theme, theme.colors.border);
    const b = node.bindings.value;
    const display = props.format === "percent" ? new Intl.NumberFormat(format.locale, { style: "percent", maximumFractionDigits: 0 }).format(frac) : props.format === "fraction" ? `${new Intl.NumberFormat(format.locale).format(value)} / ${new Intl.NumberFormat(format.locale).format(max)}` : new Intl.NumberFormat(format.locale, { maximumFractionDigits: 2 }).format(value);
    const header = h("div", { style: { display: "flex", "justify-content": "space-between", "align-items": "baseline", gap: "8px", "margin-bottom": "4px" } }, h("span", { style: { "font-weight": "600" } }, text("label")), props.showValue ? h("span", { class: ["bp-progress__value", mode === "design" && b ? "bp-cell--bound" : ""], title: b?.expr, style: { color: theme.colors.mutedText, "font-variant-numeric": "tabular-nums" } }, display) : null);
    if (props.variant === "gauge") {
      const w = 160;
      const r = 62;
      const cx = w / 2;
      const cy = 70;
      const arcPath = (f: number) => {
        const a0 = Math.PI;
        const sweep = Math.PI * f;
        const a1 = a0 + sweep;
        const x0 = cx + r * Math.cos(a0);
        const y0 = cy + r * Math.sin(a0);
        const x1 = cx + r * Math.cos(a1);
        const y1 = cy + r * Math.sin(a1);
        // The flag describes the *sweep*, not the fraction. This gauge is a
        // half-circle, so its sweep is never more than 180° and the flag is
        // always 0. Setting it from `f > 0.5` asked for an arc longer than a
        // semicircle between two points a semicircle apart, and SVG answers
        // that by moving the centre — so at 9 out of 10 the fill was drawn
        // around a different centre from the track it was supposed to fill.
        const large = sweep > Math.PI ? 1 : 0;
        return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
      };
      return h(
        "div",
        { class: "bp-progress bp-progress--gauge", style: cssText({ ...css, display: "flex", "flex-direction": "column", "align-items": "center" }) },
        h("svg", { width: w, height: 80, viewBox: `0 0 ${w} 80`, style: { display: "block" } }, h("path", { d: arcPath(1), fill: "none", stroke: track, "stroke-width": 12, "stroke-linecap": "round" }), frac > 0 ? h("path", { d: arcPath(frac), fill: "none", stroke: color, "stroke-width": 12, "stroke-linecap": "round" }) : null, h("text", { x: cx, y: cy - 4, "text-anchor": "middle", "font-size": 20, "font-weight": "700", "font-family": theme.typography.headingFont, fill: theme.colors.text }, display)),
        h("div", { style: { "font-weight": "600", "margin-top": "2px" } }, text("label")),
        props.caption ? h("div", { style: { color: theme.colors.mutedText, "font-size": "0.9em" } }, text("caption")) : null
      );
    }
    return h(
      "div",
      { class: "bp-progress bp-progress--bar", style: cssText(css) },
      header,
      // The fill's height is stated in pixels rather than as `100%`: a
      // percentage height needs a parent the layout engine considers to have a
      // definite one, and the PDF engine does not, so the fill came out a
      // hairline in the file while looking right in the browser. The track's
      // own height is the number to use, and it is right here.
      h("div", { class: "bp-progress__track", style: { height: `${props.height}px`, background: track, "border-radius": `${props.height}px`, overflow: "hidden" } }, h("div", { class: "bp-progress__fill", style: { width: `${frac * 100}%`, height: `${props.height}px`, background: color, "border-radius": `${props.height}px` } })),
      props.caption ? h("div", { style: { color: theme.colors.mutedText, "font-size": "0.9em", "margin-top": "4px" } }, text("caption")) : null
    );
  }
});
